"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { getCookie, setCookie, CookieValueTypes } from "cookies-next/client";

export const COOKIE_CONSENT_KEY = "user_cookie_consent";

type CookieConsentContext = {
  cookieConsent?: CookieValueTypes;
  acceptedCookies: boolean;
  showCookieConsentPopup: boolean;
  changeCookieConsent: (accept: boolean) => void;
  setShowCookieConsentPopup: (show: boolean) => void;
};

const CookieConsentContext = createContext<CookieConsentContext>({
  cookieConsent: undefined,
  acceptedCookies: false,
  showCookieConsentPopup: false,
  changeCookieConsent: () => {},
  setShowCookieConsentPopup: () => {},
});

export function CookieConsentContextProvider({
  children,
}: React.PropsWithChildren) {
  const [cookieConsent, setCookieConsent] = useState<CookieValueTypes>(
    getCookie(COOKIE_CONSENT_KEY)
  );
  const [acceptedCookies, setAcceptedCookies] = useState(false);
  const [showCookieConsentPopup, setShowCookieConsentPopup] = useState(false);

  useEffect(() => {
    setAcceptedCookies(cookieConsent !== "declined");
    setShowCookieConsentPopup(!cookieConsent);
  }, [cookieConsent]);

  const changeCookieConsent = async (accept: boolean) => {
    const url = process.env.NEXT_PUBLIC_SITE_URL
      ? new URL(process.env.NEXT_PUBLIC_SITE_URL)
      : null;
    if (url?.hostname) {
      const newConsent = accept ? "accepted" : "declined";

      setCookie(COOKIE_CONSENT_KEY, newConsent, {
        domain: url?.hostname,
        path: "/",
        maxAge: 60 * 60 * 24 * 365, // 1 year
      });

      setCookieConsent(newConsent);
      setShowCookieConsentPopup(false);
    }
  };

  return (
    <CookieConsentContext.Provider
      value={{
        cookieConsent,
        acceptedCookies,
        showCookieConsentPopup,
        changeCookieConsent,
        setShowCookieConsentPopup,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider"
    );
  }
  return context;
};
